import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { UrlUpload } from "@/components/admin/UrlUpload";
import { ImageGroupsEditor, GROUP_DEFAULTS } from "@/components/admin/ImageGroupsEditor";
import { VariantImages } from "@/components/admin/VariantImages";

const BLANK = {
  title: "", slug: "", category: "Original", description: "", framingDisclaimer: "All original paintings are sold unframed, as-is.",
  dimensions: { width: "", height: "", unit: "cm" }, aspectRatio: "", price: 0, isSold: false, image_groups: [], category_ids: [],
  primaryPrintImageUrl: "", variants: [], medium: "", featured: false, hidden: false, artworkSlug: "", sortOrder: 100,
};

const Variants = ({ variants, set }) => {
  const upd = (i, k, v) => set(variants.map((x, n) => (n === i ? { ...x, [k]: v } : x)));
  return (
    <div className="col-span-2">
      <span className="field-label mb-2">Sizes & pricing (size → price → Gelato product UID → photos for that size)</span>
      {variants.map((v, i) => (
        <div key={i} className="mb-3 space-y-2 border-b border-[#F0EDE6] pb-3">
          <div className="grid grid-cols-12 gap-2">
            <input className="admin-input col-span-3" placeholder="18 × 24 in" value={v.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`variant-label-${i}`} />
            <input className="admin-input col-span-2" type="number" placeholder="Price" value={v.price} onChange={(e) => upd(i, "price", e.target.value)} data-testid={`variant-price-${i}`} />
            <input className="admin-input col-span-6" placeholder="Gelato product UID" value={v.gelatoUid} onChange={(e) => upd(i, "gelatoUid", e.target.value)} data-testid={`variant-uid-${i}`} />
            <button type="button" className="col-span-1 text-xs text-[#8B2E24]" onClick={() => set(variants.filter((_, n) => n !== i))} data-testid={`variant-remove-${i}`}>✕</button>
          </div>
          <VariantImages images={v.images || []} set={(imgs) => upd(i, "images", imgs)} testid={`variant-images-${i}`} />
        </div>
      ))}
      <button type="button" className="mt-1 text-xs underline underline-offset-4" onClick={() => set([...variants, { label: "", price: "", gelatoUid: "", images: [] }])} data-testid="variant-add-btn">+ Add size</button>
    </div>
  );
};

const CategoryChecks = ({ value, onChange }) => {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="col-span-2" data-testid="product-form-categories">
      <span className="field-label mb-2">Categories</span>
      {cats.length === 0 ? (
        <p className="text-xs text-[#8A8780]">No categories yet — add them in the Categories tab.</p>
      ) : (
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {cats.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={value.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`product-form-cat-${c.slug}`} /> {c.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const initialGroups = (p) => {
  if (p.image_groups?.length) return p.image_groups.map((g) => ({ ...g, images: g.images.map((i) => ({ ...i })) }));
  if (!p.id) return [{ group_name: GROUP_DEFAULTS.Original, images: [] }];
  return [{ group_name: GROUP_DEFAULTS[p.category] || "Images", images: (p.images || []).map((url, i) => ({ url, alt: "", is_default: i === 0 })) }];
};

export const ProductForm = ({ product, onClose, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const isNew = product && !product.id;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!product) return;
    setF({
      ...BLANK, ...product, image_groups: initialGroups(product), category_ids: product.category_ids || [],
      dimensions: { ...BLANK.dimensions, ...(product.dimensions || {}) }, medium: product.medium || "", primaryPrintImageUrl: product.primaryPrintImageUrl || "",
      variants: (product.variants || []).map(({ mockup_image_url, ...v }) => ({ ...v, images: v.images?.length ? v.images : mockup_image_url ? [{ url: mockup_image_url, alt: "" }] : [] })),
    });
  }, [product]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const num = (v) => (v === "" || v === null ? null : Number(v));
    const groups = f.image_groups
      .map((g) => ({ ...g, group_name: g.group_name.trim(), images: g.images.filter((i) => i.url.trim()) }))
      .filter((g) => g.group_name || g.images.length);
    const body = {
      ...f, price: Number(f.price) || 0, sortOrder: Number(f.sortOrder) || 100, image_groups: groups, images: [],
      dimensions: { ...f.dimensions, width: num(f.dimensions.width), height: num(f.dimensions.height) },
      variants: f.variants.map((v) => ({ ...v, price: Number(v.price) || 0, images: (v.images || []).filter((x) => x.url) })),
      artworkSlug: f.artworkSlug || f.slug, medium: f.medium || null, primaryPrintImageUrl: f.primaryPrintImageUrl || null,
    };
    try {
      const { data } = isNew ? await api.post("/admin/products", body) : await api.put(`/admin/products/${product.id}`, body);
      toast.success("Artwork saved");
      onSaved(data, isNew);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const original = f.category === "Original";
  const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto rounded-none bg-[#FDFBF7]" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-light">{isNew ? "New artwork" : `Edit — ${product?.title}`}</DialogTitle>
          <DialogDescription className="text-xs">Images upload directly to Cloudinary.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <label><span className="field-label mb-1">Title</span><input required className="admin-input" value={f.title} onChange={(e) => { set("title", e.target.value); if (isNew) set("slug", slugify(e.target.value)); }} data-testid="product-form-title" /></label>
          <label><span className="field-label mb-1">Slug (unique URL)</span><input required className="admin-input" value={f.slug} onChange={(e) => set("slug", e.target.value)} data-testid="product-form-slug" /></label>
          <label><span className="field-label mb-1">Type</span>
            <select className="admin-input" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="product-form-category">
              <option>Original</option><option>Canvas Print</option><option>Fine Art Poster</option>
            </select>
          </label>
          <label><span className="field-label mb-1">Artwork group slug (links formats on one page)</span><input className="admin-input" value={f.artworkSlug || ""} onChange={(e) => set("artworkSlug", e.target.value)} data-testid="product-form-artwork-slug" /></label>
          <CategoryChecks value={f.category_ids} onChange={(v) => set("category_ids", v)} />
          <label className="col-span-2"><span className="field-label mb-1">Description</span><textarea required rows={5} className="admin-input" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="product-form-description" /></label>
          <label><span className="field-label mb-1">Medium (optional)</span><input className="admin-input" value={f.medium} onChange={(e) => set("medium", e.target.value)} placeholder="Oil on canvas" data-testid="product-form-medium" /></label>
          <div className="grid grid-cols-3 gap-2">
            <label><span className="field-label mb-1">Width</span><input type="number" step="any" className="admin-input" value={f.dimensions.width ?? ""} onChange={(e) => set("dimensions", { ...f.dimensions, width: e.target.value })} data-testid="product-form-width" /></label>
            <label><span className="field-label mb-1">Height</span><input type="number" step="any" className="admin-input" value={f.dimensions.height ?? ""} onChange={(e) => set("dimensions", { ...f.dimensions, height: e.target.value })} data-testid="product-form-height" /></label>
            <label><span className="field-label mb-1">Unit</span><input className="admin-input" value={f.dimensions.unit} onChange={(e) => set("dimensions", { ...f.dimensions, unit: e.target.value })} data-testid="product-form-unit" /></label>
          </div>
          {original ? (
            <>
              <label><span className="field-label mb-1">Price (AUD)</span><input type="number" step="any" required className="admin-input" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="product-form-price" /></label>
              <label className="flex items-end gap-2 pb-2 text-sm"><input type="checkbox" checked={f.isSold} onChange={(e) => set("isSold", e.target.checked)} data-testid="product-form-sold" /> Sold</label>
              <label className="col-span-2"><span className="field-label mb-1">Framing disclaimer</span><input className="admin-input" value={f.framingDisclaimer || ""} onChange={(e) => set("framingDisclaimer", e.target.value)} data-testid="product-form-disclaimer" /></label>
            </>
          ) : (
            <>
              <Variants variants={f.variants} set={(v) => set("variants", v)} />
              <div className="col-span-2"><UrlUpload label="Master print file (300 DPI)" value={f.primaryPrintImageUrl} onChange={(v) => set("primaryPrintImageUrl", v)} folder="art-store/prints" testid="product-form-master" accept="image/*,application/pdf" /></div>
            </>
          )}
          <ImageGroupsEditor groups={f.image_groups} set={(v) => set("image_groups", v)} />
          <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="product-form-featured" /> Feature on home page</label>
          <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.hidden} onChange={(e) => set("hidden", e.target.checked)} data-testid="product-form-hidden" /> Hidden from the collection (only reachable by direct link)</label>
          <div className="col-span-2 flex justify-end gap-3 pt-4">
            <button type="button" className="btn-outline px-6 py-3" onClick={onClose} data-testid="product-form-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary px-6 py-3" data-testid="product-form-save">{busy ? "Saving…" : "Save artwork"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
